import { SectionSummary } from '@/components/WorkspacePresentation';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { issueApi } from '@/api/endpoints';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterField,
  LoadingState,
  PageHeader,
  SeverityBadge,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { AlertIcon } from '@/components/icons';
import { filterInputClass, formatRelative } from '@/utils/format';
import type { IssueCategory, IssueSeverity, IssueStatus } from '@/types/api';

const SEVERITIES: IssueSeverity[] = ['critical', 'error', 'warning', 'opportunity', 'info'];
const CATEGORIES: IssueCategory[] = [
  'indexability',
  'http',
  'metadata',
  'content',
  'headings',
  'images',
  'structured_data',
  'internal_linking',
  'performance',
  'search_performance',
];
const STATUSES: IssueStatus[] = ['open', 'acknowledged', 'resolved', 'ignored', 'regressed'];

export function IssuesPage() {
  const { siteId = '' } = useParams();
  const [params, setParams] = useSearchParams();

  const severity = (params.get('severity') as IssueSeverity | null) ?? undefined;
  const category = (params.get('category') as IssueCategory | null) ?? undefined;
  const status = (params.get('status') as IssueStatus | null) ?? undefined;
  const search = params.get('search') ?? '';
  const page = Number(params.get('page') ?? '1');

  const query = useQuery({
    queryKey: ['issues', siteId, severity, category, status, search, page],
    queryFn: () =>
      issueApi.list(siteId, {
        severity,
        category,
        status,
        search: search || undefined,
        page,
        page_size: 50,
      }),
  });

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Any filter change invalidates the current page number.
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        title="Issues"
        description="Findings from the most recent audit. Severity reflects consequence, not how easy the problem was to detect."
      />

      {query.data && <SectionSummary items={[
        { label: 'Matching findings', value: query.data.total, detail: 'Across the current filters' },
        { label: 'High severity in view', value: query.data.items.filter(issue => ['critical', 'error'].includes(issue.severity)).length, detail: 'Critical and error findings' },
        { label: 'Open in view', value: query.data.items.filter(issue => issue.status === 'open').length, detail: 'Findings to review' },
      ]} note="Severity and status counts reflect the findings loaded on this page." />}

      <FilterBar>
          <FilterSelect
            label="Severity"
            value={severity ?? ''}
            options={SEVERITIES}
            onChange={(value) => setFilter('severity', value)}
          />
          <FilterSelect
            label="Category"
            value={category ?? ''}
            options={CATEGORIES}
            onChange={(value) => setFilter('category', value)}
          />
          <FilterSelect
            label="Status"
            value={status ?? ''}
            options={STATUSES}
            onChange={(value) => setFilter('status', value)}
          />
          <FilterField label="Search" htmlFor="issue-search" className="min-w-[12rem] flex-1">
            <input
              id="issue-search"
              defaultValue={search}
              onBlur={(event) => setFilter('search', event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') setFilter('search', event.currentTarget.value);
              }}
              placeholder="Title or check ID"
              className={filterInputClass}
            />
          </FilterField>
          {[...params.keys()].length > 0 ? (
            <button
              type="button"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
              className="pb-2 text-sm font-medium text-brand hover:underline"
            >
              Clear
            </button>
          ) : null}
      </FilterBar>

      {query.isLoading ? <LoadingState label="Loading issues" /> : null}
      {query.isError ? (
        <ErrorState message={(query.error as Error).message} onRetry={() => void query.refetch()} />
      ) : null}

      {query.data && query.data.items.length === 0 ? (
        <EmptyState
          icon={<AlertIcon className="h-5 w-5" />}
          title="No issues match"
          description="Either the audit found nothing here, or the filters are too narrow. Run an audit if you have not yet."
        />
      ) : null}

      {query.data && query.data.items.length > 0 ? (
        <Card>
          <Table>
            <thead>
              <tr>
                <Th>Issue</Th>
                <Th>Category</Th>
                <Th align="right">Affected URLs</Th>
                <Th>Status</Th>
                <Th>First seen</Th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((issue) => (
                <tr key={issue.id} className="group hover:bg-surface-muted">
                  <Td>
                    <Link
                      to={`/sites/${siteId}/issues/${issue.id}`}
                      className="flex items-start gap-2"
                    >
                      <SeverityBadge severity={issue.severity} />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-ink group-hover:text-brand">
                          {issue.title}
                        </span>
                        <span className="block font-mono text-xs text-ink-subtle">
                          {issue.check_id}
                        </span>
                      </span>
                    </Link>
                  </Td>
                  <Td>
                    <Badge tone="neutral">{issue.category.replace(/_/g, ' ')}</Badge>
                  </Td>
                  <Td align="right" className="tnum text-sm">
                    {issue.affected_url_count}
                  </Td>
                  <Td>
                    <Badge tone={issue.status === 'resolved' ? 'success' : 'neutral'}>
                      {issue.status}
                    </Badge>
                  </Td>
                  <Td className="text-xs text-ink-subtle">
                    {formatRelative(issue.first_detected_at)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          {query.data.pages > 1 ? (
            <Pagination
              page={query.data.page}
              pages={query.data.pages}
              total={query.data.total}
              onChange={(next) => setFilter('page', String(next))}
            />
          ) : null}
        </Card>
      ) : null}
    </>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const id = `filter-${label.toLowerCase()}`;
  return (
    <FilterField label={label} htmlFor={id}>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`${filterInputClass} capitalize`}
      >
        <option value="">All</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
    </FilterField>
  );
}

export function Pagination({
  page,
  pages,
  total,
  onChange,
}: {
  page: number;
  pages: number;
  total: number;
  onChange: (page: number) => void;
}) {
  return (
    <div className="workspace-pagination mt-4 flex items-center justify-between border-t border-border-subtle pt-4">
      <p className="text-xs text-ink-subtle">
        Page {page} of {pages} · {total} total
      </p>
      <div className="flex gap-2">
        <Button size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          Previous
        </Button>
        <Button size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}
